// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { adminApi } from '../api/adminApi';
import { useAdvisor } from './AdvisorContext';

interface AdminContextValue {
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  checkAdminStatus: () => Promise<void>;
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined);

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const { advisor, loading: advisorLoading } = useAdvisor();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAdminStatus = useCallback(async () => {
    // AdvisorContext hasn't resolved the profile yet (e.g. a hard/direct
    // navigation straight to an admin-gated route) — stay in the loading
    // state rather than provisionally reporting isAdmin=false. Consumers
    // gate their own redirect-away-if-not-admin effect on `loading`, so
    // collapsing to `loading=false` here before the real role is known
    // causes a false-negative bounce back to the dashboard.
    if (advisorLoading) {
      setLoading(true);
      return;
    }

    if (!advisor) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const adminStatus = await adminApi.checkAdminRole();
      setIsAdmin(adminStatus);
    } catch (err) {
      // If the call fails, user is not an admin
      setIsAdmin(false);
      // Only set error if it's not a 403 (which is expected for non-admins)
      if (err instanceof Error && !err.message.includes('403')) {
        setError('Failed to check admin status');
      }
    } finally {
      setLoading(false);
    }
  }, [advisor, advisorLoading]);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  const value = useMemo<AdminContextValue>(() => ({
    isAdmin,
    loading,
    error,
    checkAdminStatus,
  }), [isAdmin, loading, error, checkAdminStatus]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdmin = (): AdminContextValue => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export default AdminContext;
